/* eslint react-hooks/set-state-in-effect: ["warn"] */
import { useMemo, useState, useEffect, useCallback, Fragment } from "react";
import dayjs from "dayjs";
import { useToggle } from "../../../hooks/useToggle";
import { Trans, useTranslation } from "@coworker/locales";
import {
  useStoreId,
  useCountryId,
  useUID,
} from "../../../core/auth/useLoggedInUser";
import { useMyStore } from "@coworker/app/src/core/auth/useLoggedInUser";
import { useUserPreference } from "../../../hooks/useProfilePreferencesQuery";
import profilePreferences from "@coworker/enums/profilePreferences";
import FixaInlineMessage from "@coworker/apprestructured/src/shared/wrappers/FixaInlineMessage/FixaInlineMessage";
import InformationCircleIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/InformationCircleIconPath";
import {
  areaIsWholeStore,
  describeAreaFilter,
  describeDivision,
  describeFilter,
  FilterKeys,
  getDivisionFilter,
} from "../../InputPopup/UserFiltersRow/filterChoices";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { AMOUNTS, DEFAULT_AMOUNT, WHOLE_STORE_HFB } from "../constants";
import { ContentWrapper } from "../CommonComponents/layout";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { ToggleField } from "../../../core/components/widgets/ToggleField";
import styled from "styled-components";
import { PillSelection } from "../../../core/components/widgets/PillSelection";
import { ArticleSelection } from "../../../core/components/widgets/ArticleSelection";
import { AreaSelection } from "../../../core/components/widgets/AreaSelection";
import { useNumberOfArticles } from "../CommonComponents/Repositories/useArticleService";
import { ARTICLE_HASH } from "../../TestBuy/constants";
import { countNumbersOfArticles } from "./NewTestBuyForm.helper";
import {
  Division,
  TypeOfArticle,
  TypesOfArticles,
} from "@coworker/types/lib/testbuy";
import { CreateNewTestBuyResponse } from "@coworker/types/lib/testbuy_api";
import { saveChosenDivision } from "../../InputPopup/UserFiltersRow/Divisions/divisions.helpers";
import { postToTestbuyService } from "../../../hooks/API/testbuy.service";
import {
  FixaWindow,
  isChineseEnvironment,
} from "@coworker/reusable/helpers/devices";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import { useTestBuyFilters } from "../hooks/useTestBuyFilters";
import { getSelectedFilterValue } from "@coworker/apprestructured/src/shared/filters/utils/filter/filtervalue";
import {
  DEFUALT_PERIOD_VALUE,
  PERIOD_ID,
} from "../../MFAQ/hooks/usePeriodFilter";
import { PeriodFilterValue } from "@coworker/apprestructured/src/shared/filters/types/filter";
import {
  DEFAULT_LOCATION_VALUE,
  LOCATION_ID,
} from "../hooks/useLocationFilter";
import { use4A1KData } from "../data-service/useDataService";

const WidgetRowWrapper = styled("div")`
  width: 100%;
  box-shadow: 0 1px var(--grey200);
`;

const ButtonContainer = styled("div")`
  padding: 24px;
`;

const InlineMessageWrapper = styled("div")`
  margin-top: 25px;
  margin-bottom: 15px;
`;

export function NewTestBuyForm() {
  const { enableSmarterTestbuys, enableMultipleHfbsForTestbuy } =
    window as FixaWindow;

  const storeId: string = useStoreId();
  const uid = useUID();
  const countryId = useCountryId();
  const store = useMyStore();
  const isChina = isChineseEnvironment();
  const { t } = useTranslation();
  const acesAndKingCountries = use4A1KData();
  const [userLanguage] = useUserPreference(
    profilePreferences.LANGUAGE,
    store?.primaryLocale
  );
  const storeLocale = store?.primaryLocale;
  const enableDivisionsArea = false;

  const testbuyFilters = useTestBuyFilters();

  const period = getSelectedFilterValue(
    PERIOD_ID,
    testbuyFilters.filterValues,
    DEFUALT_PERIOD_VALUE
  ) as PeriodFilterValue;

  const location = getSelectedFilterValue(
    LOCATION_ID,
    testbuyFilters.filterValues,
    DEFAULT_LOCATION_VALUE
  ).value as string;

  const filters = useMemo(() => {
    return [period.value, location];
  }, [period, location]) as FilterKeys;

  const initialArea = location || WHOLE_STORE_HFB;
  const [area, setArea] = useState(initialArea);
  const [selectedAreas, setSelectedAreas] = useState<string[]>(() =>
    enableMultipleHfbsForTestbuy && !areaIsWholeStore(initialArea)
      ? [initialArea]
      : []
  );
  const [division, setDivision] = useState(getDivisionFilter(filters) ?? "");
  const [randomHFBs, setRandomHFBs] = useState<string[]>([]);
  const { numberOfArticlesForArea, loading } = useNumberOfArticles(
    storeId,
    area ?? ""
  );

  const DEFAULT_TYPE_OF_ARTICLES = ARTICLE_HASH.RANDOM;

  const [typeOfArticles, setTypeOfArticles] = useState(
    DEFAULT_TYPE_OF_ARTICLES
  );

  const [numberOfArticles, setNumberOfArticles] = useState(DEFAULT_AMOUNT);
  const [newAmountOfArticles, setNewAmountOfArticles] = useState(0);
  const [includeOutgoing, toggleIncludeOutgoing] = useToggle(true);
  const [includeOutOfStock, toggleIncludeOutOfStock] = useToggle(false);
  const [isLoading, setIsLoading] = useState(false);

  const divisions = [] as Division[];

  const onAreaSelected = async (
    selectedArea: string | { type: string; item: string } | string[]
  ) => {
    if (Array.isArray(selectedArea)) {
      // Multiple HFB selection (feature flag enabled)
      setSelectedAreas(selectedArea);

      // Set area to first non-whole-store HFB, or "whole=whole" if that's selected
      const hasWholeStore = selectedArea.some((area) => areaIsWholeStore(area));
      if (hasWholeStore) {
        setArea(WHOLE_STORE_HFB);
      } else if (selectedArea.length > 0 && selectedArea[0]) {
        setArea(selectedArea[0]);
      }
    } else if (typeof selectedArea === "object") {
      const { item, type } = selectedArea;
      const { hfbs } = saveChosenDivision(divisions, item, type);
      setRandomHFBs(hfbs);

      const selectedAreaInDivision = areaIsWholeStore(selectedArea.item)
        ? WHOLE_STORE_HFB
        : selectedArea.item;

      setArea(selectedAreaInDivision);
      setDivision(selectedArea.type);
    } else {
      setArea(selectedArea);
      setSelectedAreas([]);
    }
  };
  const onTypeOfArticlesSelected = async (selectedTypeOfArticles: {
    type: TypesOfArticles;
    transKey: string;
  }) => {
    setTypeOfArticles(selectedTypeOfArticles);
    setArea(
      isChina && selectedTypeOfArticles.type === "ACESANDKING"
        ? WHOLE_STORE_HFB
        : area
    );
    // Clear selectedAreas when switching away from Random type
    if (selectedTypeOfArticles.type !== ARTICLE_HASH.RANDOM?.type) {
      setSelectedAreas([]);
    }
  };

  useEffect(() => {
    if (isChina) setArea(WHOLE_STORE_HFB);
  }, [setArea, isChina]);

  useEffect(() => {
    countNumbersOfArticles({
      loading,
      newAmountOfArticles,
      numberOfArticlesForArea,
      typeOfArticles: typeOfArticles as TypeOfArticle,
      setNumberOfArticles,
    });
  }, [loading, newAmountOfArticles, numberOfArticlesForArea, typeOfArticles]);

  const wholeArea = area === WHOLE_STORE_HFB;

  const onNumberOfArticlesSelected = async (selectedValue: number) => {
    if (selectedValue) {
      setNumberOfArticles(selectedValue);
      setNewAmountOfArticles(selectedValue);
    }
  };
  const navigate = useFixaNavigate();
  const { showToast } = useToastNotification();

  const onSubmit = useCallback(async () => {
    if (typeOfArticles?.type !== "CUSTOM" && area) {
      const createBody = {
        numberOfArticles,
        area,
        storeId,
        includeOutgoing,
        includeOutOfStock,
        createdAt: dayjs().format("YYYY-MM-DD"),
        createdBy: uid,
        typeOfArticles: typeOfArticles?.type,
        area_new:
          enableMultipleHfbsForTestbuy &&
          typeOfArticles?.type === ARTICLE_HASH.RANDOM?.type &&
          selectedAreas.length > 0
            ? selectedAreas
            : enableDivisionsArea && areaIsWholeStore(area)
              ? randomHFBs
              : [area],
        divisionId: division,
        locale: userLanguage ?? storeLocale ?? "en_GB",
        countryId,
        isSmarterTestbuy: enableSmarterTestbuys,
      };
      try {
        setIsLoading(true);
        await postToTestbuyService(
          "testbuy/create_testbuy",
          createBody,
          (createTestbuyRes: CreateNewTestBuyResponse) => {
            setIsLoading(false);
            if (createTestbuyRes.testBuyId) {
              navigate(`/testbuy/${createTestbuyRes.testBuyId}`);
            } else {
              const message =
                createTestbuyRes?.message === "INSUFFICIENT_ARTICLES" ? (
                  <>
                    <Trans>insufficientArticles</Trans>{" "}
                    <Trans>availableArticlesForHfbString</Trans>
                    {`: ${createTestbuyRes.amountOfAvailableArticles}`}
                  </>
                ) : typeof createTestbuyRes?.message === "number" ? (
                  <>
                    <Trans
                      values={{
                        hfb: `HFB${area.split("=")[1]}`,
                      }}
                    >
                      insufficientArticlesInHFBString
                    </Trans>{" "}
                    <Trans>availableArticlesForHfbString</Trans>
                    {`: ${createTestbuyRes.amountOfAvailableArticles}`}
                  </>
                ) : (
                  <Trans>searchErrorString</Trans>
                );
              showToast(message, undefined, undefined, 5);
            }
          }
        );
      } catch (error) {
        setIsLoading(false);
        const message =
          error instanceof Error &&
          error.message === "INSUFFICIENT_ARTICLES" ? (
            <Trans>insufficientArticles</Trans>
          ) : (
            <Trans>searchErrorString</Trans>
          );
        showToast(message, undefined, undefined, 5);
      }
    }
    if (typeOfArticles?.type === "CUSTOM") {
      navigate(`/testbuy/custom/${area}`);
    }
  }, [
    area,
    includeOutgoing,
    numberOfArticles,
    navigate,
    showToast,
    storeId,
    uid,
    includeOutOfStock,
    typeOfArticles?.type,
    enableDivisionsArea,
    division,
    randomHFBs,
    userLanguage,
    countryId,
    setIsLoading,
    storeLocale,
    enableMultipleHfbsForTestbuy,
    selectedAreas,
  ]);

  function getAreaDescription(
    selectedArea: string | undefined,
    selectedDivision: string | undefined
  ) {
    if (
      enableMultipleHfbsForTestbuy &&
      typeOfArticles?.type === ARTICLE_HASH.RANDOM?.type &&
      selectedAreas.length > 0
    ) {
      if (selectedAreas.length === 1 && areaIsWholeStore(selectedAreas[0])) {
        return <Trans>wholeStoreString</Trans>;
      }
      return selectedAreas
        .map((areaKey) => {
          const description = describeFilter(areaKey, false);

          if (typeof description === "string") {
            return description;
          }
          return areaKey;
        })
        .join(", ");
    }

    if (selectedArea && selectedDivision && areaIsWholeStore(area)) {
      return <Trans>{describeDivision(selectedDivision)}</Trans>;
    } else if (area) {
      return describeAreaFilter([area], true);
    } else {
      return <Trans>selectLocationString</Trans>;
    }
  }

  const noAcesAndKingData =
    isChina ||
    (Array.isArray(acesAndKingCountries) &&
      acesAndKingCountries?.includes(countryId)) ||
    false;

  const linkbarItems = useMemo(() => {
    return [
      {
        label: t("testBuysString"),
        to: "/testbuy",
      },
    ];
  }, [t]);

  const isAcesAndKing = typeOfArticles?.type === ARTICLE_HASH.ACESANDKING?.type;
  const isRandom = typeOfArticles?.type === ARTICLE_HASH.RANDOM?.type;

  const showCustomOrRandomAndWholeStoreError =
    (typeOfArticles?.type === ARTICLE_HASH.CUSTOM?.type ||
      typeOfArticles?.type === ARTICLE_HASH.RANDOM?.type) &&
    areaIsWholeStore(area);
  return (
    <FullScreenPopup
      noPadding
      linkbar={
        <Linkbar
          items={linkbarItems}
          currentLabel={t("newTestBuy")}
          currentAsH1
        />
      }
    >
      <ContentWrapper>
        {typeOfArticles && (
          <WidgetRowWrapper>
            <ArticleSelection
              typeOfArticles={typeOfArticles}
              onConfirm={onTypeOfArticlesSelected}
            />
          </WidgetRowWrapper>
        )}
        <WidgetRowWrapper>
          <AreaSelection
            title={<Trans>location3String</Trans>}
            selected={getAreaDescription(area, division)}
            onConfirm={onAreaSelected}
            isAcesAndKing={isAcesAndKing}
            area={area}
            setArea={setArea}
            multiSelect={enableMultipleHfbsForTestbuy && isRandom}
            selectedAreas={selectedAreas}
            maxSelections={3}
          />
        </WidgetRowWrapper>
        {typeOfArticles?.type !== ARTICLE_HASH.CUSTOM?.type && (
          <>
            <WidgetRowWrapper>
              <PillSelection
                title={<Trans>numberOfArticlesString</Trans>}
                choices={AMOUNTS}
                selected={numberOfArticles}
                onConfirm={onNumberOfArticlesSelected}
                closePopupWhenSelected={
                  isAcesAndKing ||
                  (!wholeArea &&
                    typeOfArticles?.type === ARTICLE_HASH.RANDOM?.type)
                }
              />
            </WidgetRowWrapper>
            <WidgetRowWrapper>
              <ToggleField
                title={
                  <b>
                    <Trans>includeEDSArticlesString</Trans>
                  </b>
                }
                onChange={toggleIncludeOutgoing}
                value={includeOutgoing}
              />
            </WidgetRowWrapper>
            <WidgetRowWrapper>
              <ToggleField
                title={
                  <b>
                    <Trans>includeOutOfStockString</Trans>
                  </b>
                }
                onChange={toggleIncludeOutOfStock}
                value={includeOutOfStock}
              />
            </WidgetRowWrapper>
          </>
        )}
      </ContentWrapper>
      {isAcesAndKing && !noAcesAndKingData ? (
        <InlineMessageWrapper>
          <FixaInlineMessage
            title={<Trans>noAcesAndKingDataTitleString</Trans>}
            body={<Trans>noAcesAndKingDataBodyString</Trans>}
            ssrIcon={InformationCircleIconPath}
            variant={"negative"}
          />
        </InlineMessageWrapper>
      ) : (
        <Fragment>
          {showCustomOrRandomAndWholeStoreError && (
            <FixaInlineMessage
              title={<Trans>customTestBuyAndWholeStoreChosenErrorString</Trans>}
              variant={"cautionary"}
            />
          )}
          <ButtonContainer>
            <FixaButton
              type={"primary"}
              text={
                typeOfArticles?.type !== "CUSTOM" ? (
                  <Trans>createTestbuyString</Trans>
                ) : (
                  <Trans>continue2String</Trans>
                )
              }
              disabled={
                !area ||
                (areaIsWholeStore(area) &&
                  !division &&
                  typeOfArticles?.type === ARTICLE_HASH.RANDOM?.type) ||
                (areaIsWholeStore(area) &&
                  !division &&
                  typeOfArticles?.type === ARTICLE_HASH.CUSTOM?.type)
              }
              onClick={onSubmit}
              data-testid="start-test-buy"
              fluid={true}
              loading={isLoading}
            />
          </ButtonContainer>
        </Fragment>
      )}
    </FullScreenPopup>
  );
}
